/**
 * @fileoverview Login card.
 * @description The white card shown inside AuthLayout: logo, heading, subtext, the login form,
 * and the trust-signal badges. Also shows a "password changed, sign in again" notice when
 * useChangePassword sent the user here after their sessions were revoked — read once from its
 * one-shot flag (password-changed-notice.utils) and cleared, so a reload doesn't show it again.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import logoUrl from '@/assets/images/login/logo.png'
import twoFactorIcon from '@/assets/svg/login/icon-badge-2fa.svg'
import encryptedIcon from '@/assets/svg/login/icon-badge-encrypted.svg'
import fdicIcon from '@/assets/svg/login/icon-badge-fdic.svg'
import LoginForm from '@/features/auth/components/LoginForm'
import {
  clearPasswordChangedNotice,
  hasAccountActivatedNotice,
  hasPasswordChangedNotice,
} from '@/features/auth/utils/password-changed-notice.utils'
import TrustBadgeList from '@/shared/components/molecules/TrustBadgeList'

/**
 * @component
 */
function AuthCard() {
  const { t } = useTranslation()
  const [passwordChanged] = useState(hasPasswordChangedNotice)
  const [accountActivated] = useState(hasAccountActivatedNotice)

  useEffect(() => {
    clearPasswordChangedNotice()
  }, [])

  const trustBadges = [
    { icon: encryptedIcon, label: t('login.trustBadges.encrypted') },
    { icon: fdicIcon, label: t('login.trustBadges.fdicInsured') },
    { icon: twoFactorIcon, label: t('login.trustBadges.twoFactor') },
  ]

  return (
    <div className="flex w-full flex-col items-center">
      <img src={logoUrl} alt="InsightBox" className="mb-2 h-18 sm:h-22 lg:h-24 w-auto" />
      <div
        className="mt-8 w-full rounded-[18px] border border-primary/10 bg-white p-6 shadow-card sm:p-8 lg:p-10"
        data-testid="login-card"
      >
        <h1
          data-testid="login-title"
          className="text-[26px] font-bold leading-tight tracking-tight text-ink sm:text-[30px]"
        >
          {t('login.title')}
        </h1>
        <p className="mt-1.5 text-[15px] text-slate sm:text-base">{t('login.subtitle')}</p>
        {passwordChanged && (
          <p
            role="status"
            data-testid="login-password-changed-notice"
            className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-[14px] text-green-800"
          >
            {t('login.passwordChangedNotice')}
          </p>
        )}
        {accountActivated && (
          <p
            role="status"
            data-testid="login-account-activated-notice"
            className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-[14px] text-green-800"
          >
            {t('login.accountActivatedNotice')}
          </p>
        )}
        <LoginForm />
        <TrustBadgeList items={trustBadges} />
      </div>
    </div>
  )
}

export default AuthCard
